<script lang="ts">
  // Typography for long text: legal pages, help articles, release notes,
  // rendered markdown. Style plain HTML inside it; no classes needed.
  //   <Prose><h2>1. Wie we zijn</h2><p>…</p><ul><li>…</li></ul></Prose>
  //   <Prose size="sm">{@html faqAnswer}</Prose>
  import type { Snippet } from 'svelte';

  interface Props {
    size?: 'sm' | 'md';
    /** Limit the line length (default 72ch); false for full width. */
    measure?: boolean;
    class?: string;
    children: Snippet;
  }
  let { size = 'md', measure = true, class: cls = '', children }: Props = $props();
</script>

<div class="prose {size} {cls}" class:measure>{@render children()}</div>

<style>
  .prose { color: var(--text-2); font-size: var(--text-md); line-height: var(--leading-relaxed); min-width: 0; }
  .sm { font-size: var(--text-base); line-height: var(--leading-normal); }
  .measure { max-width: 72ch; }

  .prose :global(h2) { font-size: var(--text-xl); margin: var(--space-8) 0 var(--space-3); color: var(--text); }
  .prose :global(h3) { font-size: var(--text-md); margin: var(--space-5) 0 var(--space-1_5); color: var(--text); }
  .prose :global(h4) { font-size: var(--text-base); margin: var(--space-4) 0 var(--space-1); color: var(--text); }
  .prose :global(:is(h2, h3, h4):first-child) { margin-top: 0; }
  .prose :global(p) { margin: 0; }
  .prose :global(p + p), .prose :global(p + ul), .prose :global(p + ol), .prose :global(ul + p), .prose :global(ol + p) { margin-top: var(--space-2_5); }
  .prose :global(ul), .prose :global(ol) { margin: var(--space-2) 0 0; padding-left: 0; display: flex; flex-direction: column; gap: var(--space-1_5); }
  .prose :global(ul) { list-style: none; }
  .prose :global(ul > li) { position: relative; padding-left: var(--space-5); }
  .prose :global(ul > li::before) {
    content: '';
    position: absolute;
    left: var(--space-1);
    top: 0.72em;
    width: 6px;
    height: 6px;
    border-radius: var(--radius-full);
    background: var(--bird-3);
  }
  .prose :global(ol) { padding-left: var(--space-5); }
  .prose :global(ol > li::marker) { color: var(--text-3); font-variant-numeric: tabular-nums; }
  .prose :global(strong) { color: var(--text); font-weight: var(--weight-semibold); }
  .prose :global(a) {
    font-weight: var(--weight-medium);
    text-decoration: underline;
    text-decoration-color: color-mix(in srgb, var(--brand) 35%, transparent);
    text-underline-offset: 3px;
  }
  .prose :global(a:hover) { text-decoration-color: var(--brand); }
  .prose :global(code) {
    font-family: var(--font-mono);
    font-size: 0.85em;
    padding: var(--space-0_5) var(--space-1_5);
    border-radius: var(--radius-xs);
    background: var(--surface-3);
    color: var(--text);
  }
  .prose :global(blockquote) {
    margin: var(--space-4) 0;
    padding: var(--space-1) var(--space-4);
    border-left: 3px solid var(--border-brand);
    color: var(--text-2);
  }
  .prose :global(hr) { border: 0; border-top: 1px solid var(--border); margin: var(--space-6) 0; }
  .prose :global(table) {
    width: 100%;
    margin-top: var(--space-2_5);
    border-collapse: separate;
    border-spacing: 0;
    font-size: var(--text-base);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    background: var(--surface);
  }
  .prose :global(th) {
    text-align: left;
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-caps);
    color: var(--text-3);
    background: var(--surface-2);
    padding: var(--space-2_5) var(--space-3);
    border-bottom: 1px solid var(--border);
  }
  .prose :global(td) { padding: var(--space-3); vertical-align: top; line-height: var(--leading-normal); border-bottom: 1px solid var(--border); }
  .prose :global(tr:last-child td) { border-bottom: 0; }
  .prose :global(td:first-child) { color: var(--text); font-weight: var(--weight-medium); }
</style>
